/**
 * Shared per-line scanner for DATA(Q) Query syntax.
 *
 * Both highlighters — the live editor's ProseMirror decorations and the
 * static docs highlighter — run this and nothing else, so they cannot drift.
 * The scan paints a class name per character with last-write-wins semantics,
 * which means the ranges it hands back never overlap: neither consumer has to
 * care about stylesheet ordering to get the intended colour.
 *
 * Pure string logic; no ProseMirror/DOM dependencies.
 */

import { commandDefs, patterns } from "./querySyntaxTokens"

/** One class name per character of a line; `undefined` where unstyled. */
export type LineClasses = (string | undefined)[]

export interface LeadingCommand {
  /** Width of the line's leading whitespace. */
  indent: number
  command: string
  /** Everything after the command, or "" when it stands alone. */
  args: string
  argParts: string[]
}

// Leading whitespace is captured, not anchored away, so indented lines (loop
// bodies) keep their command highlighting and parameter checking.
const LEADING_COMMAND = /^([ \t]*)(\w+)(?:\s+(.+))?$/

// `DEFINE <sym>` / `FOR <var>` introduce a user-defined symbol. Group 1 is
// everything before the symbol, so its start offset is group 1's length.
const SYMBOL_DEFINITION = /^([ \t]*(?:DEFINE|FOR)[ \t]+)(\w+)\b/

const paint = (
  classes: LineClasses,
  start: number,
  end: number,
  className: string
): void => {
  for (let i = Math.max(0, start); i < Math.min(classes.length, end); i++) {
    classes[i] = className
  }
}

/** Split an argument string, keeping `<<…>>` table paths intact. */
export const splitArgs = (argsString: string): string[] => {
  const args: string[] = []
  let current = ""
  let inTablePath = false
  let i = 0

  while (i < argsString.length) {
    const char = argsString[i]
    const nextChar = argsString[i + 1]

    if (char === "<" && nextChar === "<") {
      inTablePath = true
      current += char
    } else if (char === ">" && nextChar === ">") {
      inTablePath = false
      current += char + nextChar
      i++
    } else if (!inTablePath && char === " ") {
      if (current.trim()) args.push(current.trim())
      current = ""
    } else {
      current += char
    }
    i++
  }
  if (current.trim()) args.push(current.trim())
  return args
}

/** The command a line leads with, indent-tolerant, or null. */
export const matchLeadingCommand = (line: string): LeadingCommand | null => {
  const match = LEADING_COMMAND.exec(line)
  if (!match) return null
  const args = match[3] ?? ""
  return {
    indent: match[1].length,
    command: match[2],
    args,
    argParts: splitArgs(args),
  }
}

/** Names of the symbols a script defines with DEFINE or FOR. */
export const collectSymbols = (lines: string[]): string[] => {
  const symbols = new Set<string>()
  lines.forEach((line) => {
    if (line.trimStart().startsWith("#")) return
    const match = SYMBOL_DEFINITION.exec(line)
    if (match) symbols.add(match[2])
  })
  return [...symbols]
}

// Symbols are `\w+`, so they can never carry regex metacharacters.
const compileSymbolMatchers = (symbols: string[]): RegExp[] =>
  symbols.map((symbol) => new RegExp(`\\b${symbol}\\b`, "g"))

const findRanges = (line: string, regex: RegExp): Array<[number, number]> => {
  const ranges: Array<[number, number]> = []
  regex.lastIndex = 0
  let match: RegExpExecArray | null
  while ((match = regex.exec(line)) !== null) {
    ranges.push([match.index, match.index + match[0].length])
    if (match[0].length === 0) regex.lastIndex++
  }
  return ranges
}

/**
 * Paint one `<<…>>` reference as its parts rather than a flat run:
 * delimiters recede, the name carries the reference colour, and the label
 * after the first `//` reads as a label.
 */
const paintReference = (
  classes: LineClasses,
  line: string,
  start: number,
  end: number
): void => {
  paint(classes, start, start + 2, "syntax-ref-punct")
  paint(classes, end - 2, end, "syntax-ref-punct")

  const innerStart = start + 2
  const innerEnd = end - 2
  const separator = line.slice(innerStart, innerEnd).indexOf("//")
  if (separator === -1) {
    paint(classes, innerStart, innerEnd, "syntax-qtablePath")
    return
  }
  paint(classes, innerStart, innerStart + separator, "syntax-qtablePath")
  paint(
    classes,
    innerStart + separator,
    innerStart + separator + 2,
    "syntax-ref-punct"
  )
  paint(classes, innerStart + separator + 2, innerEnd, "syntax-ref-label")
}

const scanLineWith = (line: string, symbolMatchers: RegExp[]): LineClasses => {
  const classes: LineClasses = new Array(line.length).fill(undefined)

  if (line.trim().startsWith("#")) {
    paint(classes, 0, line.length, "syntax-comment")
    return classes
  }

  const refs = findRanges(line, /<<[^>]*>>/g)
  const insideRef = (index: number): boolean =>
    refs.some(([start, end]) => index >= start && index < end)

  // 1. Leading command and its positional arguments. Arguments start as
  //    plain references; `<<…>>` ones get their structure back in step 4.
  const leading = matchLeadingCommand(line)
  if (leading && commandDefs[leading.command]) {
    const { indent, command, args, argParts } = leading
    const expectedParams = commandDefs[command].params.length
    const commandStart = indent
    paint(classes, commandStart, commandStart + command.length, "syntax-command")

    if (command === "QTABLE") {
      if (args) {
        const tablePath = args.trim()
        const pathStart = commandStart + command.length + 1
        paint(classes, pathStart, pathStart + tablePath.length, "syntax-qtablePath")
      }
    } else if (command === "FIELD") {
      if (args && argParts.length > 0) {
        const pathStart = commandStart + command.length + 1
        paint(classes, pathStart, pathStart + argParts[0].length, "syntax-qtablePath")
      }
    } else if (args) {
      let argPos = commandStart + command.length + 1
      argParts.forEach((arg, index) => {
        if (index < expectedParams) {
          while (line[argPos] === " ") argPos++
          paint(classes, argPos, argPos + arg.length, "syntax-qtablePath")
          argPos += arg.length + 1
        }
      })
    }
  }

  // 2. Token patterns. Nothing generic may tint inside a reference — only
  //    the reference parts and symbols are allowed in there.
  Object.entries(patterns).forEach(([type, regex]) => {
    if (type === "comment") return
    findRanges(line, regex).forEach(([start, end]) => {
      if (insideRef(start)) return
      paint(classes, start, end, `syntax-${type}`)
    })
  })

  // 3. Quoted strings, but not a stray quote inside a reference label.
  findRanges(line, /"[^"]*"/g).forEach(([start, end]) => {
    if (insideRef(start)) return
    paint(classes, start, end, "syntax-string")
  })

  // 3b. A FOR/DEFINE tail is literal values: iteration and exclusion values
  //     (`FOR BANK <<Banks used>> -None` — None is an excluded category) and
  //     DEFINE's substitution text. Refs, the exclude marker and symbol uses
  //     all repaint on top of this.
  const definitionMatch = SYMBOL_DEFINITION.exec(line)
  if (definitionMatch) {
    const afterSymbol = definitionMatch[1].length + definitionMatch[2].length
    const tailStart =
      afterSymbol + (/^[ \t]*/.exec(line.slice(afterSymbol))?.[0].length ?? 0)
    paint(classes, tailStart, line.length, "syntax-string")
  }

  // 4. Reference structure, which outranks both of the above inside `<<…>>`.
  refs.forEach(([start, end]) => paintReference(classes, line, start, end))
  // `::` is a path separator wherever it appears: inside a reference name,
  // in a bare Table::Field path, or between two chained references.
  findRanges(line, /::/g).forEach(([start, end]) =>
    paint(classes, start, end, "syntax-ref-punct")
  )

  const lineCommand = line.trimStart().match(/^(\w+)/)?.[1]
  if (lineCommand === "CAT") {
    // CAT's quoted argument is a category map:
    //   "<label>: <value>; <label>: <value>; …"
    // Highlight each label (the text before its colon); the values after the
    // colon stay part of the string. A + inside a label (e.g. "Young+teens")
    // is part of the label, not an operator.
    const quoteMatch = line.match(/"([^"]*)"/)
    if (quoteMatch) {
      const quoteStart = (quoteMatch.index ?? 0) + 1
      const labelScan = /([^;:"]+):/g
      let labelMatch: RegExpExecArray | null
      while ((labelMatch = labelScan.exec(quoteMatch[1])) !== null) {
        const raw = labelMatch[1]
        const label = raw.trim()
        if (!label) continue
        // Offset past any leading whitespace so the decoration covers only
        // the label text, not the "; " separator.
        const leadingSpace = raw.length - raw.trimStart().length
        const from = quoteStart + labelMatch.index + leadingSpace
        paint(classes, from, from + label.length, "syntax-cat-label")
      }
    }
  } else {
    // Expression operators — logical (AND/OR/NOT), comparison (== != >= <=),
    // arithmetic (+ - * /) and grouping parens — but only OUTSIDE `<<…>>`,
    // so the + - // inside variable labels keep the reference colours.
    // Quotes are deliberately absent: they now read as string delimiters.
    const lineContentStart = line.length - line.trimStart().length
    findRanges(line, /\b(?:AND|OR|NOT)\b|==|!=|>=|<=|[-+*/()<>]/g).forEach(
      ([start, end]) => {
        if (insideRef(start)) return
        // The leading token is the command keyword; don't recolour a
        // word-operator (AND/OR/NOT) sitting there as a command.
        const isWordOp = /^(?:AND|OR|NOT)$/.test(line.slice(start, end))
        if (isWordOp && start === lineContentStart) return
        paint(classes, start, end, "syntax-operator")
      }
    )
  }

  // 5. User-defined symbols win over everything else, including reference
  //    parts — `<<TITLE//BANK>>` should show BANK as the loop variable.
  symbolMatchers.forEach((matcher) => {
    findRanges(line, matcher).forEach(([start, end]) =>
      paint(classes, start, end, "syntax-symbol")
    )
  })
  if (definitionMatch) {
    const start = definitionMatch[1].length
    paint(classes, start, start + definitionMatch[2].length, "syntax-symbol-def")
  }

  return classes
}

/**
 * Scan a whole script. Symbols are collected across every line first, so a
 * DEFINE at the top colours its uses further down.
 */
export const scanLines = (lines: string[]): LineClasses[] => {
  const matchers = compileSymbolMatchers(collectSymbols(lines))
  return lines.map((line) => scanLineWith(line, matchers))
}

/** Scan a single line against symbols already collected for the script. */
export const scanLine = (line: string, symbols: string[]): LineClasses =>
  scanLineWith(line, compileSymbolMatchers(symbols))
